import { geminiRequest } from "./geminiRequest";
import i18n from "./i18n";

const pendingTranslations = {}; // Object to store pending translation promises

// Helper function to safely parse JSON from localStorage
function getStoredTranslations(translationKey) {
  const translationsJSON = localStorage.getItem(translationKey) || {};
  try {
    return JSON.parse(translationsJSON) || {};
  } catch (e) {
    return {};
  }
}

// Helper function to save translations to localStorage
function saveTranslation(translationKey, key, data) {
  const translations = getStoredTranslations(translationKey);
  translations[key] = data;
  try {
    localStorage.setItem(translationKey, JSON.stringify(translations));
  } catch (error) {
    if (error instanceof DOMException && error.name === "QuotaExceededError") {
      console.warn("localStorage is full. Unable to save new translations.");
      // Implement additional handling, e.g., cleanup or use fallback storage
    } else {
      throw error;
    }
  } finally {
    // Remove the promise from pendingTranslations after saving to localStorage
    delete pendingTranslations[key];
  }
}

export const fetchTranslation = async (project, data, key = "title") => {
  const title = key === "body" ? data?.body : data?.title;
  const translationKey = `translations-${i18n.resolvedLanguage}`;
  const inOriginalLanguage =
    project?.settings?.originalLanguage === i18n.resolvedLanguage;
  const storageKey = `${title}`; // Use title as the key directly or customize as needed
  const translations = getStoredTranslations(translationKey);

  let originalTranslation;
  try {
    originalTranslation = JSON.parse(data?.translation);
  } catch {
    originalTranslation = data?.translation;
  }

  if (inOriginalLanguage) {
    return title;
  }
  if (
    !inOriginalLanguage &&
    originalTranslation?.lang === i18n.resolvedLanguage
  ) {
    const originalTranslationData =
      key === "body" ? originalTranslation?.body : originalTranslation?.title;

    return originalTranslationData;
  }
  if (translations[storageKey] && translations[storageKey] !== null) {
    return translations[storageKey];
  }
  if (title && !inOriginalLanguage) {
    // Check if a translation for this title is already pending
    if (!pendingTranslations[storageKey]) {
      // If not, create a new promise for deepLRequest and store it in pendingTranslations
      pendingTranslations[storageKey] = geminiRequest({
        body: `Translate this text: ${title} into ${i18n.resolvedLanguage}`,
        assistantId: "default",
        lang: i18n.resolvedLanguage,
      })
        .then((data) => {
          saveTranslation(translationKey, storageKey, data);
          return data;
        })
        .catch((error) => {
          console.error("Error fetching translation:", error);
          throw error;
        });
    }
    // Return the existing or new promise for the translation
    return pendingTranslations[storageKey];
  }
};
